import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getMyProfile, updateMyProfile, changeMyPassword, uploadUserPhoto, API_URL } from '../services/api';
import { Save, Loader2, Key, Camera, User, Mail, Phone, Briefcase, Building2, Shield } from 'lucide-react';

const userTypes = { 0: 'Admin', 1: 'Inhouse Worker', 2: 'Contractor', 3: 'Sub Contractor', 4: 'Employee' };
const typeColors = { 0: 'bg-purple-100 text-purple-700 border-purple-200', 1: 'bg-blue-100 text-blue-700 border-blue-200', 2: 'bg-orange-100 text-orange-700 border-orange-200', 3: 'bg-slate-100 text-slate-700 border-slate-200', 4: 'bg-teal-100 text-teal-700 border-teal-200' };

function MyProfile() {
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['myProfile'],
    queryFn: () => getMyProfile().then(r => r.data),
  });

  useEffect(() => {
    if (profile && !editing) setForm(profile);
  }, [profile, editing]);

  const updateMutation = useMutation({
    mutationFn: (data) => updateMyProfile(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      setEditing(false);
      toast.success('Profile updated');
    }
  });

  const passwordMutation = useMutation({
    mutationFn: (data) => changeMyPassword(data),
    onSuccess: () => { setShowPasswordModal(false); setPasswords({ current_password: '', new_password: '', confirm_password: '' }); toast.success('Password changed successfully'); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to change password')
  });

  const photoMutation = useMutation({
    mutationFn: (formData) => uploadUserPhoto(profile.id, formData),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['myProfile'] }); toast.success('Photo updated'); }
  });

  const startEdit = () => { setForm({ ...profile }); setEditing(true); };
  const cancelEdit = () => { setForm({ ...profile }); setEditing(false); };
  const saveEdit = () => {
    updateMutation.mutate({
      first_name: form.first_name, last_name: form.last_name,
      email: form.email, phone: form.phone,
      designation: form.designation, contractor_business_name: form.contractor_business_name,
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    photoMutation.mutate(fd);
  };

  const handlePasswordChange = () => {
    if (passwords.new_password !== passwords.confirm_password) { toast.error('Passwords do not match'); return; }
    if (passwords.new_password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    passwordMutation.mutate({ current_password: passwords.current_password, new_password: passwords.new_password });
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!profile) return null;

  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username;
  const photoUrl = profile.image_url ? `${API_URL}/api/users/${profile.id}/photo?t=${profile.updated_at || ''}` : null;

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl" data-testid="my-profile-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">My Profile</h1>
        <p className="text-slate-500 mt-0.5">View and manage your account details</p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-card border" data-testid="profile-card">
        <div className="p-6 border-b bg-slate-50/50">
          <div className="flex items-start gap-5">
            <div className="relative group">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-semibold overflow-hidden">
                {photoUrl ? (
                  <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  fullName.charAt(0) || 'U'
                )}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity" data-testid="upload-profile-photo-btn">
                <Camera size={20} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </label>
              {photoMutation.isPending && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                  <Loader2 size={20} className="text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-slate-800">{fullName}</h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${typeColors[profile.user_type]}`}>
                  {userTypes[profile.user_type]}
                </span>
              </div>
              <p className="text-slate-500 mt-1">@{profile.username}</p>
              {profile.designation && <p className="text-sm text-slate-500 mt-0.5">{profile.designation}</p>}
            </div>
            <div className="flex gap-2">
              {!editing ? (
                <button onClick={startEdit} className="px-4 py-2 bg-primary text-white text-sm rounded-sm hover:bg-primary/90" data-testid="edit-profile-btn">Edit Profile</button>
              ) : (
                <>
                  <button onClick={cancelEdit} className="px-4 py-2 border text-sm rounded-sm hover:bg-slate-50" data-testid="cancel-profile-edit-btn">Cancel</button>
                  <button onClick={saveEdit} disabled={updateMutation.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-sm hover:bg-primary/90 flex items-center gap-1.5" data-testid="save-profile-btn">
                    {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Profile Fields */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ProfileField icon={User} label="First Name" value={form.first_name} editing={editing} onChange={(v) => setForm({...form, first_name: v})} testId="profile-first-name" />
            <ProfileField icon={User} label="Last Name" value={form.last_name} editing={editing} onChange={(v) => setForm({...form, last_name: v})} testId="profile-last-name" />
            <ProfileField icon={Mail} label="Email" value={form.email} editing={editing} onChange={(v) => setForm({...form, email: v})} type="email" testId="profile-email" />
            <ProfileField icon={Phone} label="Phone" value={form.phone} editing={editing} onChange={(v) => setForm({...form, phone: v})} testId="profile-phone" />
            <ProfileField icon={Briefcase} label="Designation" value={form.designation} editing={editing} onChange={(v) => setForm({...form, designation: v})} testId="profile-designation" />
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">
                <div className="flex items-center gap-2"><Shield size={14} className="text-slate-400" />User Type</div>
              </label>
              <p className="text-sm text-slate-800 py-2">{userTypes[profile.user_type]}</p>
            </div>
            {profile.user_type === 2 && (
              <ProfileField icon={Building2} label="Business Name" value={form.contractor_business_name} editing={editing} onChange={(v) => setForm({...form, contractor_business_name: v})} testId="profile-business-name" className="md:col-span-2" />
            )}
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="bg-white rounded-card border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Key size={18} className="text-amber-600" /></div>
            <div><p className="font-medium text-slate-800">Password & Security</p><p className="text-xs text-slate-500">Update your password to keep your account secure</p></div>
          </div>
          <button onClick={() => setShowPasswordModal(true)} className="px-4 py-2 border text-sm rounded-sm hover:bg-slate-50" data-testid="open-change-password-btn">Change Password</button>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-card w-full max-w-sm">
            <div className="p-5 border-b"><h3 className="text-lg font-semibold font-heading">Change Password</h3></div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Current Password</label>
                <input type="password" value={passwords.current_password} onChange={(e) => setPasswords({...passwords, current_password: e.target.value})} className="w-full px-3 py-2 border rounded-sm" data-testid="current-password-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">New Password</label>
                <input type="password" value={passwords.new_password} onChange={(e) => setPasswords({...passwords, new_password: e.target.value})} className="w-full px-3 py-2 border rounded-sm" data-testid="new-password-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Confirm New Password</label>
                <input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})} className="w-full px-3 py-2 border rounded-sm" data-testid="confirm-password-input" />
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => { setShowPasswordModal(false); setPasswords({ current_password: '', new_password: '', confirm_password: '' }); }} className="px-4 py-2 border text-sm rounded-sm" data-testid="cancel-password-change-btn">Cancel</button>
              <button onClick={handlePasswordChange} disabled={passwordMutation.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-sm disabled:opacity-50 flex items-center gap-1.5" data-testid="submit-password-change-btn">
                {passwordMutation.isPending && <Loader2 size={14} className="animate-spin" />} Change Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileField({ icon: Icon, label, value, editing, onChange, type = 'text', testId, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-slate-600 mb-1.5">
        <div className="flex items-center gap-2">{Icon && <Icon size={14} className="text-slate-400" />}{label}</div>
      </label>
      {editing ? (
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border rounded-sm text-sm" data-testid={testId} />
      ) : (
        <p className="text-sm text-slate-800 py-2" data-testid={testId}>{value || '-'}</p>
      )}
    </div>
  );
}

export default MyProfile;
